/* eslint-disable react/jsx-no-bind */
import { CircularProgress, Grid2, IconButton, Typography } from '@mui/material';
import { withStyles } from '@mui/styles';
import ClearIcon from '@mui/icons-material/Cancel';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import React, { useState } from 'react';
import { COLORS } from '../../../utils/Application_Constants';
import ProgressBarAnimation from './ProgressBarAnimation';
import { getExpenseImageById } from '../../Services/ExpenseService';
import { dataURItoBlob } from '../../Services/Common';

const styles = () => ({
    preview: {
        border: `1px solid ${COLORS.LT_MIDNIGHT10}`,
        padding: 12,
        borderRadius: 8,
        '&:hover': {
            cursor: 'pointer',
            background: COLORS.LT_MIDNIGHTBG1,
        },
    },
    previewError: {
        border: `1px solid ${COLORS.LT_PEPPER}`,
    },
    previewSuccess: {
        border: `1px solid ${COLORS.LT_EMERALD}`,
    },
    fileThumbnail: {
        width: 40,
        height: 40,
        borderRadius: '4px',
        display: 'block',
        objectFit: 'cover',
        cursor: 'pointer',

        '&.skeleton': {
            background: COLORS.LT_MIDNIGHT25,
            animation: '$pulse 2s cubic-bezier(.4,0,.6,1) infinite',
        },
    },
    fileIcon: {
        width: 28,
        height: 28,
        color: COLORS.LT_MIDNIGHT10,
    },
    errorIcon: {
        width: 16,
        height: 16,
        color: COLORS.LT_PEPPER,
    },
    successIcon: {
        width: 16,
        height: 16,
        color: COLORS.LT_EMERALD,
    },
    pdfThumbnail: {
        width: 40,
        height: 40,
        borderRadius: '4px',
        background: COLORS.LT_JOURNEY75,
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    fileName: {
        fontWeight: 'bold',
        overflow: 'hidden',
        width: '100%',
        textOverflow: 'ellipsis',
        display: 'flex',
        flexDirection: 'column',
        whiteSpace: 'nowrap',
    },
    fileNameLink: {
        '& u': {
            color: COLORS.LT_JOURNEY,
            fontWeight: 'bold',
        },
    },
    fileSize: {
        fontWeight: 'bold',
        color: COLORS.LT_MIDNIGHT50,
    },
    fileIconContainer: {
        width: 44,
        height: 44,
        borderRadius: '8px',
        background: COLORS.LT_JOURNEY,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    messageContainer: {
        display: 'flex',
        alignItems: 'center',
        marginTop: 4,
    },
    mainContainer: {
        marginTop: 10,
    },
    message: {
        marginLeft: 2,
        height: 16,
    },
    '@keyframes pulse': {
        '0%': {
            opacity: 1,
        },
        '50%': {
            opacity: 0.5,
        },
        '100%': {
            opacity: 1,
        },
    },
    descriptionIcon: {
        background: COLORS.LT_JOURNEY,
        color: COLORS.WHITE,
        width: 35,
        height: 35,
        borderRadius: 8,
    },
    descriptionIconContainer: {
        paddingRight: '12px',
    },
});

const FilePreview = (props) => {
    const {
        file,
        removeFile,
        loadingState,
        isDisabled,
        fileIndex,
        classes,
        isExisting,
        mode,
        variant,
        message,
        hideRemoveButton,
        isCombinedPdf,
    } = props;
    const initialLoadingState = { isLoading: false, duration: 0 };
    const [isPreviewLoading, setIsPreviewLoading] = useState(false);
    const { isLoading, duration } = loadingState[file.name] || initialLoadingState;
    const isPdf = file.type === 'application/pdf';
    const isCsvOrExcel =
        file.type === 'text/csv' ||
        file.type === 'application/vnd.ms-excel' ||
        file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    const bytesToMegaBytes = (bytes) => {
        if (!bytes || Number.isNaN(bytes)) return 0;
        return (bytes / (1024 * 1024)).toFixed(1);
    };
    const bytesToKiloBytes = (bytes) => {
        if (!bytes || Number.isNaN(bytes)) return 0;
        return (bytes / 1024).toFixed(1);
    };
    const getFileExtension = (fileName) => {
        if (!fileName) return '';

        const parts = fileName.split('.');

        return parts.length > 1 ? parts[parts.length - 1] : '';
    };

    const fileType = file.type ? file.type.split('/')[1] : getFileExtension(file.filename);
    const getContainerClass = () => {
        if (variant === 'error') {
            return classes.previewError;
        }
        if (variant === 'success') {
            return classes.previewSuccess;
        }
        return undefined;
    };

    async function previewImage() {
        if (mode === 'credit-card-file-upload') return;

        if (isExisting) {
            setIsPreviewLoading(true);
            try {
                const isPreCodedExpense = mode === 'pending-cc';
                const anImage = await getExpenseImageById(
                    isPreCodedExpense ? file.preCodedExpenseId : file.expenseId,
                    file.id,
                    isPreCodedExpense
                );
                const blob = dataURItoBlob(anImage.image);
                const blobUrl = URL.createObjectURL(blob);

                if (file.isCombinedPdf) {
                    // Download file with original filename
                    const link = document.createElement('a');
                    link.href = blobUrl;
                    link.download = file.originalFilename || 'download.pdf';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    window.open(blobUrl, '_blank');
                } else {
                    // Open in a new tab
                    window.open(blobUrl, '_blank');
                }

                // Cleanup blob URL to prevent memory leaks
                setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);
            } catch (error) {
                console.error('Error fetching image', error);
            } finally {
                setIsPreviewLoading(false);
            }
        } else {
            window.open(file.preview, '_blank');
        }
    }

    return (
        <div className={classes.mainContainer}>
            <Grid2
                container
                alignItems="center"
                justifyContent="space-between"
                key={!isExisting ? file.name : file.originalFilename}
                className={[classes.preview, getContainerClass()]}
            >
                <Grid2 item size={7} onClick={previewImage}>
                    <Grid2 container alignItems="center" spacing={1}>
                        <div className={classes.fileIconContainer}>
                            <DescriptionOutlinedIcon className={classes.fileIcon} />
                        </div>
                        <Grid2 size={7}>
                            {isPreviewLoading ? (
                                <CircularProgress size={20} />
                            ) : (
                                <div className={classes.fileName}>
                                    <Typography
                                        className={classes.fileNameLink}
                                        variant="caption"
                                        title={!isExisting ? file.name : file.originalFilename}
                                    >
                                        <u>{!isExisting ? file.name : file.originalFilename}</u>
                                    </Typography>
                                    <Typography variant="caption" className={classes.fileSize}>
                                        {fileType?.toUpperCase()} |{' '}
                                        {(() => {
                                            // Try multiple ways to get file size
                                            const fileSize = !isExisting
                                                ? file.size || file.filesize
                                                : file.filesize || file.size;

                                            if (!fileSize || fileSize === 0) {
                                                return 'Unknown size';
                                            }

                                            const sizeInMB = bytesToMegaBytes(fileSize);
                                            return sizeInMB < 0.1
                                                ? `${bytesToKiloBytes(fileSize)}KB`
                                                : `${sizeInMB}MB`;
                                        })()}
                                    </Typography>
                                </div>
                            )}
                            {isLoading && <ProgressBarAnimation durationSeconds={duration} />}
                        </Grid2>
                    </Grid2>
                </Grid2>
                <Grid2>
                    <Grid2 container alignItems="center" spacing={1}>
                        {!isCombinedPdf && !hideRemoveButton && (
                            <Grid2>
                                <IconButton
                                    onClick={() => removeFile(fileIndex, isExisting)}
                                    disabled={isDisabled}
                                    size="large"
                                >
                                    <ClearIcon fontSize="small" />
                                </IconButton>
                            </Grid2>
                        )}
                    </Grid2>
                </Grid2>
            </Grid2>
            {message && (
                <div className={classes.messageContainer}>
                    {variant === 'error' && (
                        <ErrorOutlineOutlinedIcon className={classes.errorIcon} />
                    )}
                    {variant === 'success' && <CheckCircleIcon className={classes.successIcon} />}
                    <Typography
                        variant="caption"
                        color={variant === 'error' ? variant : 'textSecondary'}
                        className={classes.message}
                    >
                        {message}
                    </Typography>
                </div>
            )}
        </div>
    );
};

export default withStyles(styles)(FilePreview);
